import {
  CollaborationBarLocation,
  CollaborationBarState,
  DEFAULT_COLLABORATION_BAR_STATE,
} from "./collaborationBarReducer";

const STATE_STORAGE_KEY = "CollaborationBar.state";

/**
 * Narrow a parsed JSON value back to a location, or reject it.
 *
 * Storage outlives the code that wrote it: a value left by an older shape must
 * not become malformed state the reducer then carries forward.
 */
function parseLocation(value: unknown): CollaborationBarLocation | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const { mode, threadId, view } = value as Record<string, unknown>;

  if (mode !== "ai-agent-chat") {
    return null;
  }
  if (threadId !== null && typeof threadId !== "string") {
    return null;
  }
  if (view !== "thread" && view !== "history") {
    return null;
  }

  return { mode, threadId, view };
}

/**
 * Persistent storage for collaboration bar state: the width in localStorage,
 * the address in sessionStorage.
 *
 * The address is session-scoped on purpose — reopening the app tomorrow should
 * not drop you back into yesterday's conversation, but a reload or a tab
 * restore within the session should.
 */
export const CollaborationBarStorage = {
  /** The width (in pixels) of the collaboration bar. */
  getWidth(): number | null {
    const value = localStorage.getItem("CollaborationBar.width_in_pixels");

    if (value === null) {
      return null;
    }

    const width = Number(value);
    return Number.isFinite(width) ? width : null;
  },

  /** The bar's persisted address, or the default for a bar that has never
   *  been opened. */
  getState(): CollaborationBarState {
    try {
      const raw = sessionStorage.getItem(STATE_STORAGE_KEY);
      if (raw === null) {
        return DEFAULT_COLLABORATION_BAR_STATE;
      }

      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) {
        return DEFAULT_COLLABORATION_BAR_STATE;
      }

      const { isOpen, location } = parsed as Record<string, unknown>;
      const parsedLocation = parseLocation(location);

      if (typeof isOpen !== "boolean" || parsedLocation === null) {
        return DEFAULT_COLLABORATION_BAR_STATE;
      }

      return { isOpen, location: parsedLocation };
    } catch {
      return DEFAULT_COLLABORATION_BAR_STATE;
    }
  },

  /** Set the width of the collaboration bar in pixels. */
  setWidth(value: number): void {
    localStorage.setItem("CollaborationBar.width_in_pixels", value.toString());
  },

  /** Persist the bar's address so a reload lands back where it left off. */
  setState(value: CollaborationBarState): void {
    try {
      sessionStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Ignore sessionStorage errors (private browsing, quota).
    }
  },
};
